import { DownOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { Dropdown, message, Space, Tag, Upload } from "antd";
import _ from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../../api";
import apiPaths from "../../api/paths";
import DynamicTable from "../../components/DynamicTable";
import { API_ROOT_URL } from "../../config";
import UpdatePlanModal from "./components/UpdatePlanModal";
import UserPlanModal from "./components/UserPlanModal";
import * as XLSX from "xlsx";
import HasPlanModal from "./components/HasPlanModal";

function UserPlans() {
  const [userPlans, setUserPlans] = useState({});
  const [hasPlans, setHasPlans] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [updatePlanModal, setUpdatePlanModal] = useState(false);

  const COLUMNS = [
    {
      title: "User",
      dataIndex: "user.user_email",
      width: "200px",
      filterType: "search",
      fixed: "left",
    },
    {
      title: "Plan",
      dataIndex: "plan.name",
      width: "150px",
      filterType: "search",
    },
    {
      title: "Validity",
      dataIndex: "end_time",
      // filterType: "search",
      width: "100px",
      render: (value) => {
        let diff = moment(value).diff(moment(), "day");
        return diff < 1 ? (
          <Tag color={"blue"}>Ended</Tag>
        ) : (
          <Tag color={"green"}>{`${diff} days left`}</Tag>
        );
      },
    },
    {
      title: "Start Date",
      dataIndex: "start_time",
      filterType: "date",
      width: "120px",
    },
    {
      title: "End Date",
      dataIndex: "end_time",
      filterType: "date",
      width: "120px",
    },
    {
      title: "Note",
      dataIndex: "note",
      filterType: "search",
      width: "120px",
    },
  ];

  useEffect(() => {
    fetchUserPlans();
  }, []);

  const fetchUserPlans = async () => {
    const res = await apiRequest({
      path: apiPaths.userPlans,
    });
    if (!res.error) {
      let plansObj = res.results.reduce((plans, plan) => {
        plans[plan.id + "s"] = plan;
        return plans;
      }, {});
      setUserPlans(plansObj);
    } else {
      message.error(res.error_description);
    }
  };

  const uploadUserPlans = async (userPlans) => {
    const res = await apiRequest({
      path: apiPaths.userPlans + "/upload",
      method: "POST",
      data: userPlans,
    });
    if (res.error) {
      message.error(res.error_description);
      return;
    }
    message.success("Users plans added");
    if (res.has_plans?.length) {
      setHasPlans(res.has_plans);
    }
    fetchUserPlans();
  };

  const handlePlanAdd = (data) => {
    setUserPlans({ [data.id + "s"]: data, ...userPlans });
    setShowPlanModal(false);
  };

  const handlePlanUpdate = (data) => {
    let updated = { ...userPlans };
    updated[data.id + "s"] = data;
    setUserPlans(updated);
    setUpdatePlanModal(false);
  };

  const handleFileUpload = (info) => {
    const { status } = info.file;

    if (status == "error") {
      message.error(`${info.file.name} file upload failed.`);
      return;
    }
    if (status == "done") {
      let reader = new FileReader();

      reader.onload = (e) => {
        let bstr = e.target.result;

        const wb = XLSX.read(bstr, { type: "binary", cellDates: true });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        let data = XLSX.utils.sheet_to_json(ws);
        if (!data.length) {
          message.error("Atleast file should have 1 row");
          return;
        }
        if (data.length > 100) {
          message.error("Make sure file has max 100 rows");
          return;
        }
        let hasError = 0;
        data = data.map((item) => {
          if (!item["Email"]) {
            hasError += 1;
          }
          return {
            user_fname: item["First Name"],
            user_lname: item["Last Name"],
            user_email: item["Email"],
            user_mobile: item["Mobile"],
          };
        });
        if (hasError) {
          message.error(`Email is missing on ${hasError} or more rows`);
          return;
        }
        uploadUserPlans(data);
      };

      reader.readAsBinaryString(info.file.originFileObj);
    }
  };

  const MENUITEMS = useMemo(() => {
    return [
      {
        key: "1",
        icon: <PlusOutlined />,
        label: (
          <a
            onClick={() => {
              setShowPlanModal(true);
            }}
          >
            Add User Plan
          </a>
        ),
      },
      {
        key: "2",
        label: (
          <Upload
            name="file"
            action={API_ROOT_URL + "/files"}
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={handleFileUpload}
            showUploadList={false}
          >
            <a>Upload users plans</a>
          </Upload>
        ),
        icon: <UploadOutlined />,
      },
    ];
  }, []);

  return (
    <div style={{ height: "80vh" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginRight: 10,
        }}
      >
        <Dropdown
          menu={{
            items: MENUITEMS,
          }}
        >
          <a onClick={(e) => e.preventDefault()}>
            <Space>
              Actions
              <DownOutlined />
            </Space>
          </a>
        </Dropdown>
        {/* <Tooltip title="Add User Plan">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setShowPlanModal(true);
            }}
          />
        </Tooltip> */}
      </div>
      <div style={{ height: "90%", marginTop: 30 }}>
        <DynamicTable
          dataSource={Object.values(userPlans)}
          columns={COLUMNS}
          scroll={{
            // x: 2250,
            y: "65vh",
          }}
          editHandler={(record) => setUpdatePlanModal(record)}
          pagination={{
            pageSize: 200,
          }}
          // showAction={false}
          requireEdit={true}
        />
      </div>
      {showPlanModal ? (
        <UserPlanModal
          open={true}
          onSuccess={handlePlanAdd}
          onCancel={() => {
            setShowPlanModal(false);
          }}
        />
      ) : null}
      {updatePlanModal ? (
        <UpdatePlanModal
          open={true}
          data={updatePlanModal}
          onSuccess={handlePlanUpdate}
          onCancel={() => {
            setUpdatePlanModal(false);
          }}
        />
      ) : null}
      {hasPlans ? (
        <HasPlanModal
          open={true}
          plans={hasPlans}
          onClose={() => {
            setHasPlans(false);
          }}
        />
      ) : null}
    </div>
  );
}

export default UserPlans;
